import { monthlyBalanceSchema, parseApiResponse, repeatedTransactionSchema, transactionSchema, transactionTypeSchema } from "@/api/responseSchemas";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { useAuthStore } from "@/stores/useAuthStore";
import { invalidateFinancialData } from "@/api/queryKeys";
import { captureEntityMutation } from "@/analytics/posthog";

export interface Transaction {
  id: number;
  account_id: number;
  destination_account_id?: number;
  account?: string;
  amount: number;
  category_id?: number | null;
  tag_id?: number;
  type_id: number;
  transaction_date: string;
  name: string;
  tag?: string;
  description?: string;
  ticket_img?: string | null;
  saving_plan_id?: number;
  saving_pocket_id?: number;
  installment_id?: number;
}

type TransactionInput = Omit<Transaction, "id" | "account" | "tag" | "ticket_img"> & {
  // Puede ser base64, null, string vacío "", o undefined.
  ticket_img?: string | null;
};

type TransactionPatchInput = Partial<{
  account_id: number;
  destination_account_id: number | null;
  amount: number;
  category_id: number | null;
  tag_id: number | null;
  type_id: number;
  transaction_date: string;
  name: string;
  ticket_img: string | null;
  description: string | null;
  saving_plan_id: number | null;
  saving_pocket_id: number | null;
  installment_id: number | null;
}>;

type TransactionPatchResult = { id: number } & Record<string, unknown>;

export interface BulkUpdateTransactionsInput {
  id_list: number[];
  category_id?: number;
  tag_id?: number;
  type_id?: number;
  transaction_date?: string;
  installment_id?: number;
  saving_plan_id?: number;
  saving_pocket_id?: number;
  account_id?: number;
}

export interface MonthlyBalance {
  date: string;
  balance: number;
  deposit: number;
  expense: number;
}

export interface MonthlyBalanceResponse {
  total_deposit: number;
  total_expense: number;
  total_balance: number;
  monthly_balances: MonthlyBalance[];
}

export interface TransactionType {
  id: number;
  name: string;
}

export interface RepeatedTransaction {
  name: string;
  trx_count: number;
  total_amount: number;
}

// Query hooks
export const useMonthlyBalances = (params?: TransactionsQueryParams, options?: { enabled?: boolean }) => {
  const token = useAuthStore((s) => s.token);

  return useQuery<MonthlyBalanceResponse, unknown>({
    queryKey: ["monthlyBalances", params ?? {}],
    queryFn: async () => {
      const res = await apiClient.get("/transactions/balance/breakdown", {
        params,
      });
      return parseApiResponse(monthlyBalanceSchema, res.data, "GET /transactionss/balance/breakdown");
    },
    enabled: !!token && (options?.enabled ?? true),
  });
};

export type TransactionsQueryParams = Record<string, string | number | boolean>;

const getEffectiveTransactionsParams = (params?: TransactionsQueryParams) => ({
  transaction_date: "desc",
  ...params,
});

const buildTransactionsUrl = (params?: TransactionsQueryParams) => {
  const searchParams = new URLSearchParams();
  Object.entries(getEffectiveTransactionsParams(params)).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.append(key, String(value));
    }
  });
  const queryString = searchParams.toString();
  return queryString ? `transactions/?${queryString}` : "transactions/";
};

export const useTransactions = (params?: TransactionsQueryParams) => {
  const token = useAuthStore((s) => s.token);
  const effectiveParams = getEffectiveTransactionsParams(params);

  return useQuery<Transaction[], unknown>({
    queryKey: ["transactions", effectiveParams],
    queryFn: async () => {
      const res = await apiClient.get(buildTransactionsUrl(params));
      return parseApiResponse(transactionSchema.array(), res.data, "GET /transactions");
    },
    enabled: !!token,
  });
};

export const useBillableTransactions = (params?: TransactionsQueryParams) => {
  const token = useAuthStore((s) => s.token);
  const effectiveParams = getEffectiveTransactionsParams(params);

  return useQuery<Transaction[], unknown>({
    queryKey: ["billableTransactions", effectiveParams],
    queryFn: async () => {
      const res = await apiClient.get(buildTransactionsUrl(params));
      return parseApiResponse(transactionSchema.array(), res.data, "GET /transactions");
    },
    enabled: !!token,
  });
};

export const useTransactionTypes = (options?: { enabled?: boolean }) => {
  const token = useAuthStore((s) => s.token);

  return useQuery<TransactionType[], unknown>({
    queryKey: ["transactionTypes"],
    queryFn: async () => {
      const res = await apiClient.get("/transactions/types");
      return parseApiResponse(transactionTypeSchema.array(), res.data, "GET /transactionss/types");
    },
    enabled: !!token && (options?.enabled ?? true),
    staleTime: Infinity,
  });
};

export const useRepeatedTransactions = (options?: { enabled?: boolean }) => {
  const token = useAuthStore((s) => s.token);

  return useQuery<RepeatedTransaction[], unknown>({
    queryKey: ["repeatedTransactions"],
    queryFn: async () => {
      const res = await apiClient.get("/transactions/repeated", {
        params: { trx_count: "desc" },
      });
      return parseApiResponse(repeatedTransactionSchema.array(), res.data, "GET /transactions/repeated");
    },
    enabled: !!token && (options?.enabled ?? true),
  });
};

// Mutation hooks
export const useAddTransaction = () => {
  const queryClient = useQueryClient();

  return useMutation<Transaction, unknown, TransactionInput, unknown>({
    mutationFn: async (transactionData: TransactionInput) => {
      const res = await apiClient.post("/transactions/", transactionData);
      return parseApiResponse(transactionSchema, res.data, "transaction mutation");
    },
    onSuccess: () => {
      captureEntityMutation({ entity: "transaction", operation: "create", result: "success" });
      return invalidateFinancialData(queryClient);
    },
    onError: (error) => {
      captureEntityMutation({ entity: "transaction", operation: "create", result: "failure", error });
    },
  });
};

export const useUpdateTransaction = () => {
  const queryClient = useQueryClient();

  return useMutation<TransactionPatchResult, unknown, { id: number; data: TransactionPatchInput }, unknown>({
    mutationFn: async ({ id, data }: { id: number; data: TransactionPatchInput }) => {
      const res = await apiClient.patch(`/transactions/${id}`, data);
      const parsed = transactionSchema.safeParse(res.data);
      return parsed.success ? parsed.data : { id, ...data };
    },
    onSuccess: (updatedTransaction, variables) => {
      captureEntityMutation({ entity: "transaction", operation: "update", result: "success" });
      const hasPatchValue = (key: keyof TransactionPatchInput) =>
        Object.prototype.hasOwnProperty.call(variables.data, key);
      const mergeTransaction = (transaction: Transaction): Transaction => {
        const merged = {
          ...transaction,
          ...updatedTransaction,
          ...variables.data,
        };

        return {
          ...merged,
          id: transaction.id,
          account_id: merged.account_id ?? transaction.account_id,
          amount: merged.amount ?? transaction.amount,
          type_id: merged.type_id ?? transaction.type_id,
          transaction_date: merged.transaction_date ?? transaction.transaction_date,
          name: merged.name ?? transaction.name,
          destination_account_id: hasPatchValue("destination_account_id")
            ? (variables.data.destination_account_id ?? undefined)
            : (merged.destination_account_id as number | undefined),
          category_id: hasPatchValue("category_id")
            ? (variables.data.category_id ?? undefined)
            : (merged.category_id as number | null | undefined),
          tag_id: hasPatchValue("tag_id")
            ? (variables.data.tag_id ?? undefined)
            : (merged.tag_id as number | undefined),
          saving_plan_id: hasPatchValue("saving_plan_id")
            ? (variables.data.saving_plan_id ?? undefined)
            : (merged.saving_plan_id as number | undefined),
          saving_pocket_id: hasPatchValue("saving_pocket_id")
            ? (variables.data.saving_pocket_id ?? undefined)
            : (merged.saving_pocket_id as number | undefined),
          installment_id: hasPatchValue("installment_id")
            ? (variables.data.installment_id ?? undefined)
            : (merged.installment_id as number | undefined),
          description: hasPatchValue("description")
            ? (variables.data.description ?? undefined)
            : (merged.description as string | undefined),
          ticket_img: hasPatchValue("ticket_img")
            ? (variables.data.ticket_img ?? null)
            : (merged.ticket_img as string | null | undefined),
        };
      };

      queryClient.setQueriesData<Transaction[]>({ queryKey: ["transactions"] }, (current) =>
        current?.map((transaction) =>
          transaction.id === updatedTransaction.id ? mergeTransaction(transaction) : transaction,
        ),
      );
      queryClient.setQueriesData<Transaction[]>({ queryKey: ["billableTransactions"] }, (current) =>
        current?.map((transaction) =>
          transaction.id === updatedTransaction.id ? mergeTransaction(transaction) : transaction,
        ),
      );

      void invalidateFinancialData(queryClient);
      void queryClient.refetchQueries({ queryKey: ["transactions"] });
      void queryClient.refetchQueries({ queryKey: ["billableTransactions"] });
      void queryClient.refetchQueries({ queryKey: ["monthlyBalances"] });
    },
    onError: (error) => {
      captureEntityMutation({ entity: "transaction", operation: "update", result: "failure", error });
    },
  });
};

export const useDeleteTransactions = () => {
  const queryClient = useQueryClient();

  return useMutation<void, unknown, number[], unknown>({
    mutationFn: async (transactionIds: number[]) => {
      const payload = { id_list: transactionIds };
      const response = await apiClient.delete("/transactions/", { data: payload });
      return response.data;
    },
    onSuccess: async () => {
      await invalidateFinancialData(queryClient);

      // Forzar refetch inmediato
      await queryClient.refetchQueries({ queryKey: ["transactions"] });
      await queryClient.refetchQueries({ queryKey: ["billableTransactions"] });
      await queryClient.refetchQueries({ queryKey: ["monthlyBalances"] });
    },
  });
};

export const useBulkUpdateTransactions = () => {
  const queryClient = useQueryClient();

  return useMutation<void, unknown, BulkUpdateTransactionsInput, unknown>({
    mutationFn: async (payload: BulkUpdateTransactionsInput) => {
      const response = await apiClient.patch("/transactions/", payload);
      return response.data;
    },
    onSuccess: async () => {
      captureEntityMutation({ entity: "transaction", operation: "update", result: "success" });
      await invalidateFinancialData(queryClient);
      await queryClient.refetchQueries({ queryKey: ["transactions"] });
      await queryClient.refetchQueries({ queryKey: ["billableTransactions"] });
      await queryClient.refetchQueries({ queryKey: ["monthlyBalances"] });
    },
    onError: (error) => {
      captureEntityMutation({ entity: "transaction", operation: "update", result: "failure", error });
    },
  });
};
